import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { platformRulesApi, PLATFORM_RULE_CATEGORIES } from '../../api/platformRules';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminPlatformRules.css';

const CATEGORY_OPTIONS = Object.entries(PLATFORM_RULE_CATEGORIES).map(([value, label]) => ({
    value,
    label,
}));

const EMPTY_FORM = {
    category: 'COMMUNITY_GUIDELINES',
    title: '',
    text: '',
    sortOrder: 0,
    isActive: true,
};

const AdminPlatformRules = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    // Filter
    const [filterCategory, setFilterCategory] = useState('');

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ ...EMPTY_FORM });

    const loadRules = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage - 1,
                size: pageSize,
                sortBy: 'sortOrder',
                direction: 'ASC',
                ...(filterCategory && { category: filterCategory }),
            };
            const data = await platformRulesApi.list(params);
            const list = data?.content || [];
            const total = data?.totalElements || list.length;
            setRules(list);
            setTotalItems(total);
        } catch (err) {
            setError('Не удалось загрузить правила платформы');
            console.error('[AdminPlatformRules] Failed to load:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterCategory]);

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            setFormData({
                category: item.category || 'COMMUNITY_GUIDELINES',
                title: item.title || '',
                text: item.text || '',
                sortOrder: item.sortOrder ?? 0,
                isActive: item.isActive ?? true,
            });
        } else {
            setFormData({ ...EMPTY_FORM });
        }
        setShowModal(true);
        setError(null);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingItem(null);
    };

    const handleSave = async () => {
        if (!formData.title.trim() || !formData.text.trim()) {
            setError('Заполните заголовок и текст правила');
            return;
        }
        try {
            setActionLoading(true);
            const payload = {
                category: formData.category,
                title: formData.title.trim(),
                text: formData.text.trim(),
                sortOrder: Number(formData.sortOrder) || 0,
                isActive: formData.isActive,
            };
            if (editingItem) {
                await platformRulesApi.update(editingItem.id, payload);
            } else {
                await platformRulesApi.create(payload);
            }
            await loadRules();
            handleCloseModal();
        } catch (err) {
            setError(`Не удалось сохранить: ${err.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggle = async (item) => {
        try {
            setActionLoading(true);
            await platformRulesApi.toggle(item.id);
            await loadRules();
        } catch (err) {
            setError(`Не удалось переключить статус: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Удалить правило «${item.title}»?`)) return;
        try {
            setActionLoading(true);
            await platformRulesApi.remove(item.id);
            await loadRules();
        } catch (err) {
            setError(`Не удалось удалить: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    if (loading && rules.length === 0) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Правила платформы</h1>

            {error && (
                <HrnbAlert type="danger">{error}</HrnbAlert>
            )}

            {/* Filter bar */}
            <div className="admin-platform-rules__toolbar">
                <Form.Select
                    className="admin-platform-rules__filter"
                    value={filterCategory}
                    onChange={(e) => {
                        setFilterCategory(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    <option value="">Все категории</option>
                    {CATEGORY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </Form.Select>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    + Добавить правило
                </Button>
            </div>

            <HrnbCard>
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10 на странице</option>
                        <option value={20}>20 на странице</option>
                        <option value={50}>50 на странице</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-platform-rules__table">
                    <thead>
                        <tr>
                            <th>Порядок</th>
                            <th>Категория</th>
                            <th>Заголовок</th>
                            <th>Текст</th>
                            <th>Статус</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center text-muted">
                                    Правила не найдены
                                </td>
                            </tr>
                        ) : (
                            rules.map((rule) => (
                                <tr key={rule.id}>
                                    <td>{rule.sortOrder ?? 0}</td>
                                    <td>
                                        <Badge bg="info">
                                            {rule.categoryDisplayName || PLATFORM_RULE_CATEGORIES[rule.category] || rule.category}
                                        </Badge>
                                    </td>
                                    <td><strong>{rule.title}</strong></td>
                                    <td>
                                        <div className="admin-platform-rules__text">
                                            {rule.text?.substring(0, 120)}
                                            {rule.text?.length > 120 ? '...' : ''}
                                        </div>
                                    </td>
                                    <td>
                                        <Badge bg={rule.isActive ? 'success' : 'secondary'}>
                                            {rule.isActive ? 'Активно' : 'Неактивно'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-1 mb-1"
                                            onClick={() => handleOpenModal(rule)}
                                            disabled={actionLoading}
                                        >
                                            Изменить
                                        </Button>
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            className="me-1 mb-1"
                                            onClick={() => handleToggle(rule)}
                                            disabled={actionLoading}
                                        >
                                            {rule.isActive ? 'Скрыть' : 'Показать'}
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="mb-1"
                                            onClick={() => handleDelete(rule)}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Create / Edit modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingItem ? 'Редактировать правило' : 'Новое правило платформы'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Категория</Form.Label>
                            <Form.Select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                {CATEGORY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Заголовок</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Например: Уважайте других пользователей"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Текст правила</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={formData.text}
                                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                placeholder="Подробное описание правила..."
                            />
                        </Form.Group>

                        <div className="d-flex gap-3">
                            <Form.Group className="mb-3" style={{ maxWidth: 150 }}>
                                <Form.Label>Порядок сортировки</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.sortOrder}
                                    onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3 d-flex align-items-end">
                                <Form.Check
                                    type="switch"
                                    label="Активно"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            </Form.Group>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={actionLoading}>
                        {actionLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminPlatformRules;
